import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

function formatTimestamp(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useI18n();
  const [comments, setComments] = useState([]);
  const [filter, setFilter] = useState("all"); // all | visible | removed
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Snapshot of unread IDs on first load so 'NEW' badge survives until mark-all-read
  const [newIds, setNewIds] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/comments", { params });
      setComments(data.comments || []);
      setUnread(data.unread || 0);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!user?.is_admin) return;
    load();
  }, [user, load]);

  // Capture which IDs were unread on first successful load
  useEffect(() => {
    if (!user?.is_admin || newIds.size > 0) return;
    if (comments.length === 0) return;
    const ids = new Set(comments.filter((c) => !c.viewed_by_admin && c.status === "visible").map((c) => c.id));
    // Always re-tag by looking at created_at after a threshold instead — but simpler:
    setNewIds(new Set(comments.filter((c) => c.status === "visible").slice(0, unread).map((c) => c.id)));
  }, [comments, unread, newIds, user]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/comments/mark-all-read");
      setUnread(0);
      setNewIds(new Set());
      toast.success("OK");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin_confirm_remove"))) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      setComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: "removed" } : c)));
      toast.success("Removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/admin/comments/${id}/restore`);
      setComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: "visible", flag_reason: null } : c)));
      toast.success("Restored.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const visibleCount = useMemo(() => comments.filter((c) => c.status === "visible").length, [comments]);

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4] font-mono-civic text-xs uppercase tracking-widest" data-testid="admin-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4] px-5" data-testid="admin-unauthorized">
        <div className="bg-white border-2 border-black p-6 text-center max-w-md">
          <div className="font-mono-civic text-xs uppercase tracking-widest text-[#E00000] mb-2" style={{ fontWeight: 900 }}>
            {t("admin_unauthorized")}
          </div>
          <Link to="/home" className="underline text-sm" style={{ fontWeight: 800 }}>
            ← Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2" style={{ fontWeight: 900 }}>
          <span className="w-2 h-2 bg-[#E00000] rounded-full" />
          {t("admin_tag")}
        </div>
        <h1 className="mt-2 font-serif-display text-black leading-[0.9] text-5xl sm:text-6xl" style={{ fontWeight: 800 }} data-testid="admin-heading">
          {t("admin_h")}
        </h1>
        <p className="mt-3 text-black max-w-2xl" style={{ fontWeight: 700 }}>
          {t("admin_intro")}
        </p>

        {/* Manual unlock tool */}
        <ManualUnlockTool t={t} />

        {/* Toolbar */}
        <div className="mt-8 bg-white border-2 border-black p-4 flex flex-wrap items-center gap-3" data-testid="admin-toolbar">
          <div className="flex items-stretch border-2 border-black">
            {["all", "visible", "removed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border-r-2 border-black last:border-r-0 ${filter === f ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
                style={{ fontWeight: 900 }}
                data-testid={`admin-filter-${f}`}
              >
                {t(f === "all" ? "admin_filter_all" : f === "visible" ? "admin_filter_visible" : "admin_filter_removed")}
              </button>
            ))}
          </div>
          {unread > 0 && (
            <span className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white px-2 py-1" style={{ fontWeight: 900 }} data-testid="admin-unread-badge">
              {t("admin_unread_badge").replace("{n}", unread.toString())}
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={markAllRead}
            className="text-xs uppercase tracking-widest px-3 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
            style={{ fontWeight: 900 }}
            data-testid="admin-mark-read"
          >
            {t("admin_mark_read")}
          </button>
          <button
            onClick={load}
            className="text-xs uppercase tracking-widest px-3 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
            style={{ fontWeight: 900 }}
            data-testid="admin-refresh"
          >
            {t("admin_refresh")}
          </button>
        </div>

        {error && (
          <div className="mt-6 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="admin-error">{error}</div>
        )}

        <div className="mt-6 font-mono-civic text-xs uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }} data-testid="admin-count">
          {visibleCount} {t("admin_filter_visible").toLowerCase()} · {comments.length - visibleCount} {t("admin_filter_removed").toLowerCase()}
        </div>

        {loading ? (
          <div className="mt-6 font-mono-civic text-xs uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
            Loading…
          </div>
        ) : comments.length === 0 ? (
          <div className="mt-6 border-2 border-dashed border-black p-8 text-center font-mono-civic text-xs uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }} data-testid="admin-empty">
            {t("admin_empty")}
          </div>
        ) : (
          <ul className="mt-6 space-y-4" data-testid="admin-comments-list">
            {comments.map((c) => {
              const isNew = newIds.has(c.id);
              const isRemoved = c.status === "removed";
              return (
                <li
                  key={c.id}
                  className={`bg-white border-2 ${isRemoved ? "border-black/40 opacity-70" : "border-black"} p-4 sm:p-5`}
                  data-testid={`admin-comment-${c.id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isNew && (
                        <span className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white px-2 py-0.5" style={{ fontWeight: 900 }} data-testid={`admin-new-${c.id}`}>
                          {t("admin_badge_new")}
                        </span>
                      )}
                      <span className={`font-mono-civic text-[10px] uppercase tracking-widest px-2 py-0.5 ${isRemoved ? "bg-black/20 text-black" : "bg-black text-white"}`} style={{ fontWeight: 900 }}>
                        {t(isRemoved ? "admin_status_removed" : "admin_status_visible")}
                      </span>
                      <span className="font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 800 }} data-testid={`admin-author-${c.id}`}>
                        {c.author_email}
                      </span>
                    </div>
                    <span className="font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
                      {formatTimestamp(c.created_at, lang)}
                    </span>
                  </div>
                  <p className="text-black whitespace-pre-wrap break-words" style={{ fontWeight: 600 }}>
                    {c.body}
                  </p>
                  {c.flag_reason && (
                    <div className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-[#E00000]" style={{ fontWeight: 800 }}>
                      {t("admin_flag_reason")} {c.flag_reason}
                    </div>
                  )}
                  {c.removed_by && (
                    <div className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
                      {t("admin_removed_by")} {c.removed_by}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    {!isRemoved ? (
                      <button
                        onClick={() => remove(c.id)}
                        className="bg-[#E00000] text-white uppercase tracking-widest px-3 py-1.5 border-2 border-[#E00000] text-xs hover:bg-white hover:text-[#E00000] transition-colors"
                        style={{ fontWeight: 900 }}
                        data-testid={`admin-remove-${c.id}`}
                      >
                        {t("admin_btn_remove")}
                      </button>
                    ) : (
                      <button
                        onClick={() => restore(c.id)}
                        className="bg-black text-white uppercase tracking-widest px-3 py-1.5 border-2 border-black text-xs hover:bg-white hover:text-black transition-colors"
                        style={{ fontWeight: 900 }}
                        data-testid={`admin-restore-${c.id}`}
                      >
                        {t("admin_btn_restore")}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


function ManualUnlockTool({ t }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const target = email.trim();
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(target)}/unlock`);
      toast.success(`${t("admin_unlock_ok")} (${data.user_email})`);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="mt-6 bg-white border-2 border-black p-4" data-testid="admin-unlock-tool">
      <summary className="cursor-pointer font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }}>
        🔓 {t("admin_unlock_h")}
      </summary>
      <p className="mt-3 text-sm text-black" style={{ fontWeight: 600 }}>
        {t("admin_unlock_intro")}
      </p>
      <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("admin_unlock_placeholder")}
          required
          autoComplete="off"
          className="flex-1 border-2 border-black p-2.5 font-mono-civic text-sm text-black focus:outline-none focus:bg-[#FFF8DC]"
          style={{ fontWeight: 600 }}
          data-testid="admin-unlock-input"
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="bg-[#E00000] text-white uppercase tracking-widest px-4 py-2.5 border-2 border-[#E00000] text-xs hover:bg-white hover:text-[#E00000] transition-colors disabled:opacity-40"
          style={{ fontWeight: 900 }}
          data-testid="admin-unlock-submit"
        >
          {busy ? t("admin_unlock_unlocking") : t("admin_unlock_btn")}
        </button>
      </form>
    </details>
  );
}
